import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { createProperty, getProperty, updateProperty } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Upload, Building2, Crown, Lock } from 'lucide-react';
import { MapPinPicker } from '@/components/MapPinPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AddPropertyPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  const [form, setForm] = useState({
    title: '',
    description: '',
    property_type: '',
    custom_property_type: '',
    listing_type: 'buy',
    price: '',
    price_unit: 'total',
    area: '',
    area_unit: 'sqft',
    bedrooms: '',
    bathrooms: '',
    parking: '',
    furnishing: '',
    floor: '',
    total_floors: '',
    facing: '',
    age: '',
    amenities: [],
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: '',
    longitude: '',
    images: [],
    videos: [],
    virtual_tour_url: '',
    owner_type: user?.user_type === 'agent' ? 'agent' : 'owner',
    owner_phone: user?.phone || ''
  });

  const [newImageUrl, setNewImageUrl] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState('');
  const [premiumStatus, setPremiumStatus] = useState(null);
  const [premiumLoading, setPremiumLoading] = useState(true);

  useEffect(() => {
    // Check premium status when on Add (not on Edit)
    if (isEdit) {
      setPremiumLoading(false);
      return;
    }
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setPremiumLoading(false); return; }
        const res = await fetch(`${API}/premium/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setPremiumStatus(data);
        }
      } catch (e) {
        console.error('Premium status fetch failed', e);
      } finally {
        setPremiumLoading(false);
      }
    };
    fetchStatus();
  }, [isEdit]);

  const amenitiesList = [
    'Swimming Pool', 'Gym', 'Garden', 'Parking', 'Security', '24/7 Water',
    'Power Backup', 'Lift', 'Club House', 'Children Play Area', 'Jogging Track',
    'Tennis Court', 'Basketball Court', 'Indoor Games', 'Party Hall', 'Intercom',
    'CCTV', 'Fire Safety', 'Rainwater Harvesting', 'Visitor Parking', 'Spa', 'Sauna'
  ];

  useEffect(() => {
    if (isEdit) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    setFetching(true);
    try {
      const data = await getProperty(id);
      const KNOWN_TYPES = ['apartment', 'villa', 'house', 'plot', 'commercial'];
      const isCustomType = data.property_type && !KNOWN_TYPES.includes(data.property_type);
      setForm({
        ...data,
        property_type: isCustomType ? 'others' : data.property_type,
        custom_property_type: isCustomType ? data.property_type : '',
        price: data.price?.toString() || '',
        area: data.area?.toString() || '',
        bedrooms: data.bedrooms?.toString() || '',
        bathrooms: data.bathrooms?.toString() || '',
        parking: data.parking?.toString() || '',
        floor: data.floor?.toString() || '',
        total_floors: data.total_floors?.toString() || '',
        latitude: data.latitude?.toString() || '',
        longitude: data.longitude?.toString() || ''
      });
    } catch (error) {
      toast.error('Failed to load property');
      navigate('/dashboard');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (key, value) => {
    setForm({ ...form, [key]: value });
  };

  const handlePin = ({ lat, lng, city, state, pincode }) => {
    setForm(prev => ({
      ...prev,
      latitude: String(lat),
      longitude: String(lng),
      city: prev.city || city || '',
      state: prev.state || state || '',
      pincode: prev.pincode || pincode || ''
    }));
  };

  const handleAmenityToggle = (amenity) => {
    setForm(prev => ({
      ...prev,
      amenities: prev.amenities.includes(amenity)
        ? prev.amenities.filter(a => a !== amenity)
        : [...prev.amenities, amenity]
    }));
  };

  const addImageUrl = () => {
    if (form.images.length >= 10) { toast.error('You can add up to 10 images'); return; }
    if (newImageUrl && !form.images.includes(newImageUrl)) {
      setForm({ ...form, images: [...form.images, newImageUrl] });
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setForm({ ...form, images: form.images.filter((_, i) => i !== index) });
  };

  const addVideoUrl = () => {
    if (form.videos.length >= 3) { toast.error('You can add up to 3 videos'); return; }
    const m = newVideoUrl.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    const url = m ? `https://www.youtube.com/embed/${m[1]}` : newVideoUrl;
    if (url && !form.videos.includes(url)) {
      setForm({ ...form, videos: [...form.videos, url] });
      setNewVideoUrl('');
    }
  };

  const removeVideo = (index) => {
    setForm({ ...form, videos: form.videos.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!form.title || !form.property_type || !form.listing_type || !form.price || !form.area || !form.address || !form.city || !form.state || !form.pincode) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (form.property_type === 'others' && !form.custom_property_type.trim()) {
      toast.error('Please specify the property type');
      return;
    }
    if (!/^(\+?\d{1,3}[-\s]?)?\d{10}$/.test((form.owner_phone || '').replace(/\s/g, ''))) {
      toast.error('Please enter a valid contact number buyers can reach you on');
      document.querySelector('[data-testid="property-owner-phone"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (form.latitude === '' || form.longitude === '' || Number.isNaN(parseFloat(form.latitude))) {
      toast.error('Please pin the property location on the map');
      document.querySelector('[data-testid="map-pin-picker"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const resolvedType = form.property_type === 'others'
      ? form.custom_property_type.trim()
      : form.property_type;

    const propertyData = {
      ...form,
      property_type: resolvedType,
      price: parseFloat(form.price),
      area: parseFloat(form.area),
      bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
      bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
      parking: form.parking ? parseInt(form.parking) : null,
      floor: form.floor ? parseInt(form.floor) : null,
      total_floors: form.total_floors ? parseInt(form.total_floors) : null,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null
    };
    delete propertyData.custom_property_type;

    setLoading(true);
    try {
      if (isEdit) {
        await updateProperty(id, propertyData);
        toast.success('Property updated successfully');
      } else {
        await createProperty(propertyData);
        toast.success('Property listed successfully');
      }
      navigate('/dashboard?tab=properties');
    } catch (error) {
      toast.error(error.message || 'Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  if (fetching || premiumLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#0F172A]"></div>
      </div>
    );
  }

  // Premium gate (only on create flow)
  if (!isEdit) {
    const canList = premiumStatus?.is_premium && (
      premiumStatus.is_unlimited_listings || (premiumStatus.listings_quota_remaining || 0) > 0
    );
    if (!canList) {
      const isExpired = premiumStatus?.is_premium === false && premiumStatus?.subscription_history?.length > 0;
      const isOutOfQuota = premiumStatus?.is_premium && !premiumStatus?.is_unlimited_listings && (premiumStatus?.listings_quota_remaining || 0) <= 0;
      return (
        <div className="min-h-screen bg-[#F9F9F7] pb-16 flex items-start justify-center" data-testid="premium-gate">
          <div className="container-luxury max-w-2xl">
            <div className="bg-white rounded-sm shadow-md border border-border/50 p-10 md:p-14 text-center">
              <div className="h-20 w-20 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-[#D4AF37]" />
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-medium mb-4">
                <Crown className="h-3.5 w-3.5" /> Premium required
              </div>
              <h1 className="font-['Plus_Jakarta_Sans'] text-3xl md:text-4xl font-bold text-[#0F172A] mb-3">
                {isOutOfQuota ? 'Listing quota exhausted' : isExpired ? 'Your plan has expired' : 'Upgrade to list properties'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {isOutOfQuota
                  ? 'You\'ve used all the listings in your current plan. Upgrade or top-up to add more properties.'
                  : 'Listing on Bharat Estate is reserved for premium members. Choose a plan starting at just ₹99/month and start posting your properties in seconds.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/premium')}
                  className="bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-semibold px-8 py-6"
                  data-testid="upgrade-cta-btn"
                >
                  <Crown className="h-4 w-4 mr-2" /> View Plans
                </Button>
                <Link to="/dashboard">
                  <Button variant="outline" className="px-8 py-6 w-full">Back to Dashboard</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16">
      <div className="container-luxury max-w-4xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate(-1)} className="h-10 w-10 rounded-sm bg-white border flex items-center justify-center hover:bg-gray-50">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A]">
              {isEdit ? 'Edit Property' : 'List Your Property'}
            </h1>
            <p className="text-muted-foreground">Fill in the details below</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Basic Information</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="title">Property Title *</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  placeholder="e.g., Luxurious 3BHK Apartment in Bandra West"
                  className="mt-2"
                  data-testid="property-title"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label>Property Type *</Label>
                  <Select value={form.property_type} onValueChange={(v) => handleChange('property_type', v)}>
                    <SelectTrigger className="mt-2" data-testid="property-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className="z-[60]">
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="villa">Villa</SelectItem>
                      <SelectItem value="house">Independent House</SelectItem>
                      <SelectItem value="plot">Plot / Land</SelectItem>
                      <SelectItem value="commercial">Commercial Space</SelectItem>
                      <SelectItem value="others">Others (specify)</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.property_type === 'others' && (
                    <Input
                      value={form.custom_property_type}
                      onChange={(e) => handleChange('custom_property_type', e.target.value)}
                      placeholder="e.g., Hospital, School, Warehouse, Farmhouse"
                      className="mt-3"
                      data-testid="custom-property-type"
                    />
                  )}
                </div>

                <div>
                  <Label>Listing Type *</Label>
                  <Select value={form.listing_type} onValueChange={(v) => handleChange('listing_type', v)}>
                    <SelectTrigger className="mt-2" data-testid="listing-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="buy">For Sale</SelectItem>
                      <SelectItem value="rent">For Rent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe your property in detail..."
                  rows={5}
                  className="mt-2"
                  data-testid="property-description"
                />
              </div>
            </div>
          </div>

          {/* Price & Area */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Price & Area</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="price">Price (₹) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={form.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="e.g., 15000000"
                  className="mt-2"
                  data-testid="property-price"
                />
              </div>

              <div>
                <Label>Price Type</Label>
                <Select value={form.price_unit} onValueChange={(v) => handleChange('price_unit', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="total">Total Price</SelectItem>
                    <SelectItem value="per_sqft">Per Sq.ft</SelectItem>
                    <SelectItem value="per_month">Per Month (Rent)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="area">Area *</Label>
                <Input
                  id="area"
                  type="number"
                  value={form.area}
                  onChange={(e) => handleChange('area', e.target.value)}
                  placeholder="e.g., 1500"
                  className="mt-2"
                  data-testid="property-area"
                />
              </div>

              <div>
                <Label>Area Unit</Label>
                <Select value={form.area_unit} onValueChange={(v) => handleChange('area_unit', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sqft">Sq.ft</SelectItem>
                    <SelectItem value="sqm">Sq.m</SelectItem>
                    <SelectItem value="sqyd">Sq.yd</SelectItem>
                    <SelectItem value="acres">Acres</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Property Details</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <Label htmlFor="bedrooms">Bedrooms</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={form.bedrooms}
                  onChange={(e) => handleChange('bedrooms', e.target.value)}
                  placeholder="e.g., 3"
                  className="mt-2"
                  data-testid="property-bedrooms"
                />
              </div>

              <div>
                <Label htmlFor="bathrooms">Bathrooms</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={form.bathrooms}
                  onChange={(e) => handleChange('bathrooms', e.target.value)}
                  placeholder="e.g., 2"
                  className="mt-2"
                  data-testid="property-bathrooms"
                />
              </div>

              <div>
                <Label htmlFor="parking">Parking Spots</Label>
                <Input
                  id="parking"
                  type="number"
                  value={form.parking}
                  onChange={(e) => handleChange('parking', e.target.value)}
                  placeholder="e.g., 1"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Furnishing</Label>
                <Select value={form.furnishing} onValueChange={(v) => handleChange('furnishing', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="furnished">Furnished</SelectItem>
                    <SelectItem value="semi-furnished">Semi-Furnished</SelectItem>
                    <SelectItem value="unfurnished">Unfurnished</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="floor">Floor Number</Label>
                <Input
                  id="floor"
                  type="number"
                  value={form.floor}
                  onChange={(e) => handleChange('floor', e.target.value)}
                  placeholder="e.g., 5"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="total_floors">Total Floors</Label>
                <Input
                  id="total_floors"
                  type="number"
                  value={form.total_floors}
                  onChange={(e) => handleChange('total_floors', e.target.value)}
                  placeholder="e.g., 20"
                  className="mt-2"
                />
              </div>

              <div>
                <Label>Facing</Label>
                <Select value={form.facing} onValueChange={(v) => handleChange('facing', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="North">North</SelectItem>
                    <SelectItem value="South">South</SelectItem>
                    <SelectItem value="East">East</SelectItem>
                    <SelectItem value="West">West</SelectItem>
                    <SelectItem value="North-East">North-East</SelectItem>
                    <SelectItem value="North-West">North-West</SelectItem>
                    <SelectItem value="South-East">South-East</SelectItem>
                    <SelectItem value="South-West">South-West</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Property Age</Label>
                <Select value={form.age} onValueChange={(v) => handleChange('age', v)}>
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Under Construction">Under Construction</SelectItem>
                    <SelectItem value="New">New (0-1 years)</SelectItem>
                    <SelectItem value="1-5 years">1-5 years</SelectItem>
                    <SelectItem value="5-10 years">5-10 years</SelectItem>
                    <SelectItem value="10+ years">10+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Location</h2>
            
            <div className="space-y-6">
              <div>
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={form.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  placeholder="e.g., 123, ABC Society, Bandra West"
                  className="mt-2"
                  data-testid="property-address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={form.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="e.g., Mumbai"
                    className="mt-2"
                    data-testid="property-city"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    value={form.state}
                    onChange={(e) => handleChange('state', e.target.value)}
                    placeholder="e.g., Maharashtra"
                    className="mt-2"
                    data-testid="property-state"
                  />
                </div>

                <div>
                  <Label htmlFor="pincode">Pincode *</Label>
                  <Input
                    id="pincode"
                    value={form.pincode}
                    onChange={(e) => handleChange('pincode', e.target.value)}
                    placeholder="e.g., 400050"
                    className="mt-2"
                    data-testid="property-pincode"
                  />
                </div>
              </div>

              <div className="pt-2">
                <Label htmlFor="owner_phone">Contact number for buyers *</Label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">Buyers will Call / WhatsApp you directly on this number for this listing.</p>
                <Input
                  id="owner_phone"
                  required
                  value={form.owner_phone}
                  onChange={(e) => handleChange('owner_phone', e.target.value)}
                  placeholder="e.g., +91 98765 43210"
                  className="mt-1 max-w-xs"
                  data-testid="property-owner-phone"
                />
              </div>

              <div className="pt-2">
                <Label>Pin the exact location on the map *</Label>
                <p className="text-sm text-muted-foreground mt-1 mb-3">Buyers see this pin and can get directions. Search a landmark, click on the map, or use “Locate address”.</p>
                <MapPinPicker
                  address={form.address}
                  city={form.city}
                  state={form.state}
                  pincode={form.pincode}
                  lat={form.latitude === '' ? null : parseFloat(form.latitude)}
                  lng={form.longitude === '' ? null : parseFloat(form.longitude)}
                  onChange={handlePin}
                />
              </div>
            </div>
          </div>

          {/* Amenities */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Amenities</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {amenitiesList.map((amenity) => (
                <div key={amenity} className="flex items-center space-x-2">
                  <Checkbox
                    id={amenity}
                    checked={form.amenities.includes(amenity)}
                    onCheckedChange={() => handleAmenityToggle(amenity)}
                    data-testid={`amenity-${amenity.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                  <Label htmlFor={amenity} className="text-sm font-normal cursor-pointer">
                    {amenity}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Images */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Images</h2>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Enter image URL (e.g., https://...)"
                  className="flex-1"
                  data-testid="image-url-input"
                />
                <Button type="button" onClick={addImageUrl} variant="outline" data-testid="add-image-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add
                </Button>
              </div>

              {form.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {form.images.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Property ${index + 1}`}
                        className="w-full h-32 object-cover rounded-sm"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Invalid+URL'; }}
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute top-2 right-2 h-6 w-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-sm text-muted-foreground">
                Add up to 10 high-quality images of your property. Use direct image URLs.
              </p>

              <div className="pt-2">
                <Label>Video tour links (optional)</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newVideoUrl}
                    onChange={(e) => setNewVideoUrl(e.target.value)}
                    placeholder="YouTube link, e.g. https://youtu.be/..."
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addVideoUrl(); } }}
                    data-testid="video-url-input"
                  />
                  <Button type="button" onClick={addVideoUrl} variant="outline" data-testid="add-video-btn">Add video</Button>
                </div>
                {form.videos.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {form.videos.map((url, index) => (
                      <div key={index} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`video-item-${index}`}>
                        <span className="truncate text-slate-600">{url}</span>
                        <button type="button" onClick={() => removeVideo(index)} className="text-rose-500 ml-2">Remove</button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">Add up to 3 walkthrough videos (YouTube).</p>
              </div>
            </div>
          </div>

          {/* Virtual Tour */}
          <div className="bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A] mb-6">Virtual Tour (Optional)</h2>
            
            <div>
              <Label htmlFor="virtual_tour_url">Virtual Tour URL</Label>
              <Input
                id="virtual_tour_url"
                value={form.virtual_tour_url}
                onChange={(e) => handleChange('virtual_tour_url', e.target.value)}
                placeholder="e.g., https://my.matterport.com/show/?m=..."
                className="mt-2"
                data-testid="virtual-tour-url"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Add a 360° virtual tour link from Matterport, YouTube, or similar platforms
              </p>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} className="flex-1 h-12">
              Cancel
            </Button>
            <Button type="submit" className="flex-1 h-12 btn-primary" disabled={loading} data-testid="submit-property-btn">
              {loading ? 'Saving...' : isEdit ? 'Update Property' : 'List Property'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPropertyPage;
